import React, {useState} from 'react'
import CreateAddress from '@components/Address'
import {pageComponents} from '@data/fragments/website/__generated__/pageComponents'
import {getCategoriesAndProductsCPGsWeb_cached_categories} from '@data/queries/categories/__generated__/getCategoriesAndProductsCPGsWeb_cached'
import {getCategoriesAndProductsWeb_cached_categories} from '@data/queries/categories/__generated__/getCategoriesAndProductsWeb_cached'
import {getMyOrderPreferences_preferences_store} from '@data/queries/preferences/__generated__/getMyOrderPreferences'
import {
  getWebsiteMenuInformation_cached_design,
  getWebsiteMenuInformation_cached_website
} from '@data/queries/website/__generated__/getWebsiteMenuInformation_cached'
import useQuery from '@hooks/useMyOrderPreferencesWithDefault'
import Container from '@packages/justo-parts/lib/components/Container'
import dynamic from 'next/dynamic'
import {useRouter} from 'next/router'

import Loading from './Products/ProductsContent/Loading'
import Track from './Track'
import useLayout from './useLayout'
import useProductsLayout from './useProductsLayout'

import styles from './styles.module.css'

const OnTableModal = dynamic(() => import('./OnTableModal'), {ssr: false})
const PageMeta = dynamic(() => import('./PageMeta'), {ssr: false})
const Page = dynamic(() => import('@page-components/Page'), {ssr: false})
const Cart = dynamic(() => import('./Cart'))
const CartMenuV2 = dynamic(() => import('./CartMenuV2'))
const CategoriesLayout = dynamic(() => import('./CategoriesLayout'))
const NextOpenDate = dynamic(() => import('./NextOpenDate'))
const NoMenu = dynamic(() => import('./NoMenu'))
const Options = dynamic(() => import('./Options'))
const Products = dynamic(() => import('./Products'))

export interface OrderProps {
  website: getWebsiteMenuInformation_cached_website
  design: getWebsiteMenuInformation_cached_design
  categories?:
    | getCategoriesAndProductsWeb_cached_categories
    | getCategoriesAndProductsCPGsWeb_cached_categories
  productId?: string
  page?: pageComponents
  componentIdToSsrProps?: {[componentId: string]: any}
}

export default function Order(props: OrderProps) {
  const {website, design, productId, categories, page, componentIdToSsrProps} = props
  const {preferences: queryPreferences, loading} = useQuery(website)
  const preferences = queryPreferences
    ? {
        ...queryPreferences,
        store: queryPreferences.storeWithSlots as getMyOrderPreferences_preferences_store
      }
    : null
  const layout = useLayout(website)
  const productCardDesign = design?.design.productCardDesign
  const productsLayout = useProductsLayout(website)
  const router = useRouter()
  const {addressModal} = router.query
  const [modalOpen, setModalOpen] = useState<boolean>(!!addressModal)

  if (!preferences) {
    return (
      <div className={styles.loadingContainer}>
        <Container>
          <Loading />
        </Container>
      </div>
    )
  }

  const menuId = preferences.menuId
  if (!menuId) return <NoMenu />
  if (!website.deliveryTypes) return null
  if (!website.deliveryTypes.length) return null

  const nextOpenDate = <NextOpenDate store={preferences.store} />

  const products =
    productsLayout === 'categories' ? (
      <CategoriesLayout
        {...preferences}
        website={website}
        menuId={menuId}
        // @ts-expect-error ts-migrate(2322) FIXME: Type '{ website: getWebsiteMenuInformation_cached_... Remove this comment to see the full error message
        nextOpenDate={nextOpenDate}
        preferences={preferences}
        productCardDesign={productCardDesign}
      />
    ) : (
      <Products
        {...preferences}
        website={website}
        productsLayout={productsLayout}
        layout={layout}
        menuId={menuId}
        loading={loading}
        nextOpenDate={nextOpenDate}
        preferences={preferences}
        productCardDesign={productCardDesign}
        preloadedCategories={categories}
      />
    )

  return (
    <div className={styles.container}>
      {modalOpen && <CreateAddress closeModal={() => setModalOpen(false)} />}
      <OnTableModal />
      {page ? (
        <Page pageId={page._id} page={page} componentIdToSsrProps={componentIdToSsrProps} />
      ) : null}
      {layout === 'mobile' ? (
        <>
          <Cart loading={loading} website={website} preferences={preferences} layout={layout} />
          <div className={styles.mobileMarginTop} />
        </>
      ) : null}
      {layout === 'v2' ? (
        <CartMenuV2 loading={loading} website={website} preferences={preferences} layout={layout} />
      ) : null}
      <Container>
        {preferences.store && layout !== 'v2' ? nextOpenDate : null}
        {layout === 'mobile' ? (
          <div>
            <Options preferences={preferences} website={website} layout={layout} />
            {products}
          </div>
        ) : null}
        {layout === 'v1' ? (
          <div className="row">
            <div className="col-xs-12 col-sm-6 col-md-4 last-sm">
              <Options preferences={preferences} website={website} layout={layout} />
              {layout === 'mobile' ? null : (
                <Cart
                  loading={loading}
                  website={website}
                  preferences={preferences}
                  layout={layout}
                />
              )}
            </div>
            <div className="col-xs-12 col-sm-6 col-md-8">{products}</div>
          </div>
        ) : null}
      </Container>
      {layout === 'v2' ? <div>{products}</div> : null}
      {productId ? null : <PageMeta website={website} />}
      <Track />
    </div>
  )
}
